import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { ContentStats } from './AdminContentDashboard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface CommentsOverviewProps {
  loading: boolean;
  stats?: ContentStats['comments'];
}

const COLORS = ['#8884d8', '#82ca9d'];

const CommentsOverview: React.FC<CommentsOverviewProps> = ({ loading, stats }) => {
  // Prepare data for comment type distribution
  const commentTypeData = stats ? [
    { name: 'Comments', value: stats.total },
    { name: 'Replies', value: stats.replies }
  ] : [];

  // Prepare data for monthly trend
  const monthlyData = stats?.byMonth ? 
    Object.entries(stats.byMonth)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count })) : [];

  // Get top 5 most commented papers
  const topPapers = stats?.byPaper 
    ? [...stats.byPaper]
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
    : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="col-span-1 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Comment Types</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={commentTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {commentTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comments Over Time */}
        <Card className="col-span-1 md:col-span-2 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Comments Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[350px] w-full" />
            ) : (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Comments" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Most Commented Papers */}
      <Card className="col-span-1 md:col-span-3 w-full">
        <CardHeader>
          <CardTitle className="text-lg">Most Commented Papers</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Paper Title</TableHead>
                  <TableHead className="text-right">Comments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topPapers.length > 0 ? (
                  topPapers.map((paper) => (
                    <TableRow key={paper.paperId}>
                      <TableCell className="font-medium">
                        <Link to={`/paper/${paper.paperId}`} className="hover:underline text-blue-600">
                          {paper.paperTitle.length > 60 
                            ? `${paper.paperTitle.substring(0, 60)}...` 
                            : paper.paperTitle}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{paper.count}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-muted-foreground">
                      No comment data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommentsOverview;
